import { ArrowLeftOutlined, CheckCircleFilled, SoundOutlined } from '@ant-design/icons'
import { Alert, Button, Image, Skeleton, Tag, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  confirmVirtualIpWorkflow,
  getVirtualIpDraft,
  getVirtualIpPositioningProfile,
  getVirtualIpVoiceWorkspace,
  type SaasApiError,
  type VirtualIpDraft,
  type VirtualIpPositioningProfile,
  type VirtualIpVoiceWorkspace,
} from '../../api'
import VirtualIpStepper from './VirtualIpStepper'
import { VIRTUAL_IP_FLOW_TOTAL_STEPS } from './virtualIpFlow'
import { useResolvedVirtualIpProgress } from './useResolvedVirtualIpProgress'
import { useVirtualIpWorkflowContext } from './VirtualIpWorkflowContext'
import { virtualIpFirstRegenerationNotice } from './virtualIpProgress'
import './virtualIp.css'

function asText(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function voiceModeLabel(voice: VirtualIpVoiceWorkspace | null, t: (key: string) => string): string {
  const mode = voice?.voice_mode || voice?.profile?.voice_mode
  if (mode === 'NONE') return t('step7.voiceModes.NONE')
  if (mode === 'AI_GENERATED') return t('step7.voiceModes.AI_GENERATED')
  if (mode === 'REFERENCE') return t('step7.voiceModes.REFERENCE')
  if (mode === 'CLONED') return t('step7.voiceModes.CLONED')
  return t('step7.voiceModes.unset')
}

export default function VirtualIpStep7Page() {
  const { t } = useTranslation('virtualIp')
  const { workspaceId = '', projectId = '' } = useParams()
  const navigate = useNavigate()
  const progress = useResolvedVirtualIpProgress()
  const { refreshProgress } = useVirtualIpWorkflowContext()
  const regenNotice = virtualIpFirstRegenerationNotice(progress)

  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [draft, setDraft] = useState<VirtualIpDraft | null>(null)
  const [profile, setProfile] = useState<VirtualIpPositioningProfile | null>(null)
  const [voice, setVoice] = useState<VirtualIpVoiceWorkspace | null>(null)

  const load = useCallback(() => {
    if (!workspaceId) return
    setLoading(true)
    setLoadError(null)
    getVirtualIpDraft(workspaceId, projectId)
      .then((d) => {
        setDraft(d.data)
        return Promise.all([
          getVirtualIpPositioningProfile(workspaceId, d.data.id).then((r) => setProfile(r.data)),
          getVirtualIpVoiceWorkspace(workspaceId, d.data.id).then((r) => setVoice(r.data)),
        ])
      })
      .catch((e) => setLoadError((e as SaasApiError).message || t('common.loadFailed')))
      .finally(() => setLoading(false))
  }, [workspaceId, t])

  useEffect(() => {
    load()
  }, [load])

  const selectedCharacter = voice?.selected_character
  const workflowConfirmed = Boolean(draft?.workflow_confirmed_at)
  const positioningSummary = profile?.confirmed_profile ?? profile?.profile_preview ?? null
  const positioningName = asText(positioningSummary?.name)
  const positioningIpType =
    asText(positioningSummary?.selected_ip_type_display_name) || asText(positioningSummary?.selected_ip_type)
  const positioningOneLiner = asText(positioningSummary?.one_liner)

  const onConfirmWorkflow = async () => {
    if (!draft) return
    setConfirming(true)
    try {
      const res = await confirmVirtualIpWorkflow(workspaceId, draft.id)
      setDraft(res.data)
      await refreshProgress()
      message.success(t('step7.workflowComplete'))
    } catch (e) {
      message.error((e as SaasApiError).message || t('common.confirmFailed'))
    } finally {
      setConfirming(false)
    }
  }

  return (
    <div className="tc-vip-page tc-vip-step7-final-page" data-testid="virtual-ip-step7-final-page">
      <div className="tc-vip-step4-topbar">
        <div>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-6`)}
            data-testid="vip-step7-back-button"
          >
            {t('common.backToPrevious')}
          </Button>
          <div className="tc-page-header tc-vip-step4-topbar-titles" style={{ marginBottom: 0 }}>
            <h1 className="tc-page-title">{t('common.pageTitle')}</h1>
            <div className="tc-vip-step-label">
              <span>{t('common.stepOf', { current: 7, total: VIRTUAL_IP_FLOW_TOTAL_STEPS })}</span>
              <span className="tc-vip-step-title">{t('step7.title')}</span>
            </div>
            <p className="tc-page-desc">{t('step7.description')}</p>
          </div>
        </div>
      </div>

      <VirtualIpStepper currentStep={7} progress={progress} />

      {regenNotice && (
        <Alert
          type="warning"
          showIcon
          message={t('common.stepsNeedRegeneration')}
          description={t(regenNotice.reasonKey)}
          style={{ marginBottom: 16 }}
        />
      )}

      {loading && <Skeleton active paragraph={{ rows: 8 }} />}
      {!loading && loadError && <Alert type="error" showIcon message={t('common.loadFailed')} description={loadError} />}

      {!loading && !loadError && (
        <div className="tc-vip-layout">
          <div className="tc-vip-main">
            <div className="tc-data-card tc-vip-card" data-testid="vip-step7-positioning-summary">
              <div className="tc-vip-card-title">{t('step7.positioningSummary')}</div>
              <dl className="tc-vip-analysis-dl">
                <dt>{t('fields.positioningPlan')}</dt>
                <dd>{positioningName || t('common.emDash')}</dd>
                <dt>{t('fields.ipType')}</dt>
                <dd>{positioningIpType || t('common.emDash')}</dd>
                <dt>{t('fields.oneLiner')}</dt>
                <dd>{positioningOneLiner || t('common.emDash')}</dd>
              </dl>
            </div>

            <div className="tc-data-card tc-vip-card" data-testid="vip-step7-character-summary">
              <div className="tc-vip-card-title">{t('step7.finalCharacter')}</div>
              {selectedCharacter?.thumbnail_url ? (
                <Image src={selectedCharacter.thumbnail_url} alt="" width={160} style={{ borderRadius: 8 }} />
              ) : null}
              <p>
                {selectedCharacter?.candidate_label || selectedCharacter?.slot_label || t('step7.noCharacterConfirmed')}
              </p>
            </div>

            <div className="tc-data-card tc-vip-card" data-testid="vip-step7-voice-summary">
              <div className="tc-vip-card-title">
                <SoundOutlined style={{ marginRight: 6 }} />
                {t('step7.voiceConfig')}
              </div>
              <Tag color={voice?.voice_mode === 'NONE' ? 'default' : 'blue'}>{voiceModeLabel(voice, t)}</Tag>
              {voice?.voice_mode === 'NONE' || voice?.profile?.voice_mode === 'NONE' ? (
                <p className="tc-vip-card-hint">{t('step7.noneVoiceHint')}</p>
              ) : null}
            </div>

            {workflowConfirmed ? (
              <Alert
                type="success"
                showIcon
                icon={<CheckCircleFilled />}
                message={t('step7.workflowComplete')}
                description={t('step7.confirmedAt', { time: draft?.workflow_confirmed_at || '' })}
                data-testid="vip-step7-workflow-complete-banner"
              />
            ) : (
              <Button
                type="primary"
                size="large"
                loading={confirming}
                disabled={!progress.completed_steps.includes(6)}
                onClick={() => void onConfirmWorkflow()}
                data-testid="vip-step7-final-confirm-button"
              >
                {t('step7.confirmAndFinish')}
              </Button>
            )}
          </div>

          <aside className="tc-vip-aside">
            <div className="tc-data-card tc-vip-card">
              <div className="tc-vip-card-title">{t('step7.confirmNotesTitle')}</div>
              <ul className="tc-vip-tips-list">
                <li>{t('step7.note1')}</li>
                <li>{t('step7.note2')}</li>
              </ul>
            </div>
          </aside>
        </div>
      )}
    </div>
  )
}
